// Admin > Feature usage tab — one dashboard covering:
//   - Activity: DAU / WAU / MAU + 30-day daily signup + login trend
//   - Features: distinct users per feature (bar chart + table)
//   - Dog mix: donut of dog_type
//   - Funnel: signup → dog → activity → 7d/30d active
//   - Power users: top 10 by activity score
//
// Data comes from a single call to GET /api/admin/stats/features.
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SectionTitle } from "@/components/app/Bits";
import { RefreshCw, Users, Activity, TrendingUp, Zap } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { fmt } from "./_helpers";


// Brand palette hooks so charts inherit YDD's colors instead of recharts default blues.
const BRAND = {
  primary: "#1C3A63",     // navy
  primaryLight: "#3D5A85",
  accent: "#CC9C33",      // brass
  muted: "#9aa2b1",
  cream: "#F6F1E8",
};
const DOG_MIX_COLORS = ["#1C3A63", "#CC9C33", "#5B7A55", "#7B4878", "#2E6F73", "#A24F2E", "#9aa2b1"];


function StatTile({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="border-border shadow-none rounded-lg" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
            <Icon size={18} />
          </div>
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
            <p className="font-display text-3xl font-extrabold tracking-tight mt-1">{value ?? "—"}</p>
            {sub ? <p className="text-xs text-muted-foreground mt-1">{sub}</p> : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


function FunnelBar({ label, count, denom, pctOfPrev, testid }) {
  const pctOfSignup = denom > 0 ? Math.round((count / denom) * 100) : 0;
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between text-sm mb-1">
        <span className="font-medium">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          <strong className="text-foreground">{count.toLocaleString()}</strong>
          {denom > 0 && <> · {pctOfSignup}% of signups</>}
          {pctOfPrev !== null && pctOfPrev !== undefined && (
            <> · <span className="text-primary">{pctOfPrev}% pass-through</span></>
          )}
        </span>
      </div>
      <div className="h-4 bg-muted rounded overflow-hidden">
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${Math.max(pctOfSignup, denom > 0 && count > 0 ? 2 : 0)}%` }}
        />
      </div>
    </div>
  );
}


export default function FeatureUsageTab() {
  const [state, setState] = useState({ loading: true, data: null, error: "" });

  const load = async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const { data } = await api.get("/admin/stats/features");
      setState({ loading: false, data, error: "" });
    } catch (err) {
      setState({ loading: false, data: null, error: err?.response?.data?.detail || "Could not load feature stats" });
    }
  };
  useEffect(() => { load(); }, []);

  if (state.loading) {
    return <p className="text-sm text-muted-foreground py-10 text-center" data-testid="feature-usage-loading">Crunching numbers…</p>;
  }
  if (state.error || !state.data) {
    return (
      <div className="py-10 text-center space-y-3" data-testid="feature-usage-error">
        <p className="text-sm text-destructive">{state.error}</p>
        <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Retry</Button>
      </div>
    );
  }

  const { activity, features, power_users: powerUsers, dog_mix: dogMix, funnel, generated_at: generatedAt } = state.data;

  // Merge signup + login series into one recharts-friendly shape so both
  // lines share an x-axis. Uses the signup series as the spine (both are
  // 31 days long from the backend anyway).
  const trendData = activity.signup_daily.map((row, i) => ({
    date: row.date.slice(5),    // MM-DD for compact axis labels
    signups: row.count,
    logins: activity.login_daily[i]?.count ?? 0,
  }));

  // Feature usage sorted desc by distinct_users for the bar chart.
  const featuresSorted = [...features].sort((a, b) => b.distinct_users - a.distinct_users);
  // Filter out any zero-user features so the chart doesn't get diluted.
  const featureBarData = featuresSorted.filter((f) => f.distinct_users > 0).map((f) => ({
    label: f.label,
    users: f.distinct_users,
    events: f.total_events,
  }));

  const totalSignups = funnel.signed_up || 0;
  const pctThruDog = totalSignups > 0 ? Math.round((funnel.added_dog / totalSignups) * 100) : 0;
  const pctThruLogged = funnel.added_dog > 0 ? Math.round((funnel.logged_something / funnel.added_dog) * 100) : 0;
  const pctThruActive7 = funnel.logged_something > 0 ? Math.round((funnel.active_7d / funnel.logged_something) * 100) : 0;

  return (
    <div className="space-y-8" data-testid="feature-usage-tab">
      {/* Header row w/ refresh */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-muted-foreground" data-testid="feature-usage-generated">
          Snapshot: {fmt(generatedAt)}
        </p>
        <Button variant="outline" size="sm" onClick={load} data-testid="feature-usage-refresh">
          <RefreshCw size={13} className="mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Activity tiles */}
      <div>
        <SectionTitle overline="Activity" title="Daily / weekly / monthly users" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatTile testid="stat-dau" icon={Activity} label="Active today" value={activity.dau} sub="Distinct users in the last 24h" />
          <StatTile testid="stat-wau" icon={Activity} label="Active this week" value={activity.wau} sub="Distinct users last 7 days" />
          <StatTile testid="stat-mau" icon={Users} label="Active this month" value={activity.mau} sub="Distinct users last 30 days" />
          <StatTile
            testid="stat-stickiness"
            icon={TrendingUp}
            label="Stickiness"
            value={activity.mau > 0 ? `${Math.round((activity.dau / activity.mau) * 100)}%` : "—"}
            sub="DAU / MAU — higher is stickier"
          />
        </div>
      </div>

      {/* 30-day trend chart */}
      <div>
        <SectionTitle overline="Last 30 days" title="Signups + active users, daily" />
        <Card className="border-border shadow-none rounded-lg" data-testid="feature-usage-trend-chart">
          <CardContent className="p-4 pl-2">
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{ top: 10, right: 16, bottom: 0, left: -12 }}>
                  <defs>
                    <linearGradient id="signupsFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={BRAND.primary} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={BRAND.primary} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="loginsFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={BRAND.accent} stopOpacity={0.4} />
                      <stop offset="95%" stopColor={BRAND.accent} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e6dec8" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: BRAND.muted }} interval={4} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: BRAND.muted }} width={40} />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #e6dec8", borderRadius: 6, fontSize: 12 }}
                    labelStyle={{ color: BRAND.primary, fontWeight: 600 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Area type="monotone" dataKey="signups" stroke={BRAND.primary} strokeWidth={2} fill="url(#signupsFill)" name="New signups" />
                  <Area type="monotone" dataKey="logins" stroke={BRAND.accent} strokeWidth={2} fill="url(#loginsFill)" name="Users active" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2 pl-4">
              Active-user counts started tracking when this feature deployed — expect flat data for the first few days.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Funnel */}
      <div>
        <SectionTitle overline="Onboarding funnel" title="Where do people drop off?" />
        <Card className="border-border shadow-none rounded-lg" data-testid="feature-usage-funnel">
          <CardContent className="p-6 space-y-4">
            <FunnelBar testid="funnel-signup" label="Signed up" count={funnel.signed_up} denom={totalSignups} pctOfPrev={null} />
            <FunnelBar testid="funnel-added-dog" label="Added a dog" count={funnel.added_dog} denom={totalSignups} pctOfPrev={pctThruDog} />
            <FunnelBar testid="funnel-logged" label="Logged something (any feature)" count={funnel.logged_something} denom={totalSignups} pctOfPrev={pctThruLogged} />
            <FunnelBar testid="funnel-active-7d" label="Active in last 7 days" count={funnel.active_7d} denom={totalSignups} pctOfPrev={pctThruActive7} />
            <FunnelBar testid="funnel-active-30d" label="Active in last 30 days" count={funnel.active_30d} denom={totalSignups} pctOfPrev={null} />
          </CardContent>
        </Card>
      </div>

      {/* Feature usage side-by-side: chart + table */}
      <div>
        <SectionTitle overline="Feature usage" title="Distinct users per feature" />
        <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
          <Card className="border-border shadow-none rounded-lg" data-testid="feature-usage-bar-chart">
            <CardContent className="p-4 pl-1">
              <div className="w-full" style={{ height: Math.max(240, featureBarData.length * 32) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={featureBarData} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e6dec8" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: BRAND.muted }} allowDecimals={false} />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 11, fill: BRAND.primary }} width={150} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #e6dec8", borderRadius: 6, fontSize: 12 }}
                      formatter={(value, name) => [value, name === "users" ? "Distinct users" : "Total events"]}
                    />
                    <Bar dataKey="users" fill={BRAND.primary} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border shadow-none rounded-lg" data-testid="feature-usage-table">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3">Feature</th>
                    <th className="text-right font-semibold px-4 py-3">Users</th>
                    <th className="text-right font-semibold px-4 py-3">Events</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {featuresSorted.map((f) => (
                    <tr key={f.key} data-testid={`feature-row-${f.key}`}>
                      <td className="px-4 py-2">{f.label}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold">{f.distinct_users}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{f.total_events}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dog mix + Power users */}
      <div className="grid gap-4 lg:grid-cols-[2fr_3fr]">
        <div>
          <SectionTitle overline="Dog mix" title="What types of dogs are on YDD?" />
          <Card className="border-border shadow-none rounded-lg" data-testid="dog-mix-card">
            <CardContent className="p-4">
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={dogMix}
                      dataKey="count"
                      nameKey="type"
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {dogMix.map((entry, i) => (
                        <Cell key={entry.type} fill={DOG_MIX_COLORS[i % DOG_MIX_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #e6dec8", borderRadius: 6, fontSize: 12 }}
                      formatter={(value, name) => [value, String(name).charAt(0).toUpperCase() + String(name).slice(1)]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <SectionTitle overline="Power users" title="Top 10 by activity score" />
          <Card className="border-border shadow-none rounded-lg" data-testid="power-users-card">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3">User</th>
                    <th className="text-left font-semibold px-4 py-3">Joined</th>
                    <th className="text-right font-semibold px-4 py-3">Score</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {powerUsers.map((u) => (
                    <tr key={u.id} data-testid={`power-user-${u.id}`}>
                      <td className="px-4 py-2">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{u.name || u.email}</p>
                          {u.name && u.email ? <p className="text-xs text-muted-foreground truncate">{u.email}</p> : null}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{u.created_at ? fmt(u.created_at).split("·")[0].trim() : "—"}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold">{u.score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-muted-foreground p-3 border-t border-border">
                Score = 3×dogs + 1×puppy_logs + 2×reminders + 1×litters + 1×titles.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
